import React, { useEffect, useState } from "react";
import {
  Button,
  Typography,
  Space,
  message,
  Spin,
} from "antd";
import {
  SettingOutlined,
  PoweroffOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import "./styles/Mapping.module.scss";
import { Icon } from 'Components';
import ReviewMappingContainer from "./ReviewMapping/ReviewMappingContainer";
import { MappingTableContainer } from "./MappingTable/MappingTableContainer";
import { GetGroupsListRequest, GetGroupsListResponse } from "Services/Api/Groups/Types";
import { DEFAULT_TABLE_REQUEST_VALUES } from "Pages/Groups/Group/Group";
import { useLazyQuery } from "@apollo/client";
import { GET_GROUPS_LIST } from "Services/Api/Groups/Queries";
import CreateMappingContainer from "./CreateMapping/CreateMappingContainer";
import { PermissionCode } from "Services/Api/Users/Types";
import { MappingViewTable } from "./MappingTable/MappingViewTable";
import { usePermission } from "Components/Permission/Permission.hooks";
import { getExistingMappedMerchants, getExternalMerchants, getProvidersForTenant, updateMappingStatus } from "Services/Api/PciCompliance/Mapping.Service";
import { authStore } from "Stores/AuthStore";
import { SystemRoleType } from "Services/Api/Roles/Types";
import { UnMappingContainer } from "./UnMapping/UnMappingContainer";

const { Text } = Typography;
export type MappingStatus = "active" | "inactive" | "outdated";

export interface MappingItem {
  id?: string;
  venzaPropertyId?: string | null | undefined;
  venzaProperty?: string | null | undefined;
  encytroOffice?: string | null | undefined;
  encytroMid?: string | null | undefined;
  action?: "" | "confirm" | "remove" | "remap" | null;
  status?: "" | "matched" | "needs review" | "removed" | "not started";
  venzaPropertyError?: string;
  encytroOfficeError?: string;
  isAddRow?: boolean;
}

export interface MappingSetting {
  key: string,
  platform: React.ReactNode,
  status: string,
  propertiesMapped: number,
  lastUpdated: string,
  providerId: number
}
export interface TenantMapping {
  id: string,
  name: string,
  providerId: number | null;
}

export interface MappingChange {
  id?: string;
  venzaProperty?: string | null | undefined;
  pciPortalProperty?: string | null;
  venzaPropertyId?: string | null;
  pciPortalPropertyId?: string | null | undefined;
  mid?: string | null;
  pciPortalPropertyError?: string | null | undefined;
  changeType?: "new" | "unchanged";
  action?: "" | "confirm" | "remove" | "remap" | null;
  venzaPropertyError?: string;
  isAddRow?: boolean;
  status?: MappingStatus;
}

export interface PropertyGroups {
  id?: string;
  isProperty?: boolean | null;
  name?: string | null;
  parentId?: string | null;
}

interface VenzaProperty {
  id: string | number;
  propertyName: string;
}

type AppState = "settings" | "new-mapping" | "review-existing" | "complete" | "view-mapping" | "unmapped";

const Mappings = () => {

  const [appState, setAppState] = useState<AppState>("settings");
  const [selectedTenantId, setSelectedTenantId] = useState<string>("");
  const [selectedTenantName, setSelectedTenantName] = useState<string>("");
  const [providerId, setProviderId] = useState<number | null>();
  const [mappings, setMappings] = useState<MappingItem[]>([]);
  const [mappingChanges, setMappingChanges] = useState<MappingChange[]>([]);
  const [loadingStates, setLoadingStates] = useState<Record<string, 'manage' | 'toggle' | null>>({});
  const [providerList, setProviderList] = useState<MappingSetting[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [existingData, setExisingData] = useState<MappingChange[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [initialVenzaProperties, setInitialVenzaProperties] = useState<VenzaProperty[]>([]);
  const { getHasPermission } = usePermission();

  const isSuperAdmin =
    authStore.currentUser?.primarySystemRole?.role ===
    SystemRoleType.SuperAdmin;

  const [getGroup, result] = useLazyQuery<
    GetGroupsListResponse,
    GetGroupsListRequest
  >(GET_GROUPS_LIST, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  });
  const [getPropertyGroup, propertiesGroupResult] = useLazyQuery<
    GetGroupsListResponse,
    GetGroupsListRequest
  >(GET_GROUPS_LIST, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  });

  const TENANTS: { label: string; value: string }[] =
    result?.data?.listGroups?.map(el => ({
      label: el.name,
      value: String(el.id),
    })) ?? [];

  useEffect(() => {
    if (!isSuperAdmin && TENANTS.length > 0 && !selectedTenantId) {
      setSelectedTenantId(TENANTS[0].value);
    }
  }, [TENANTS]);

  const PROPERTY_TENANTS: PropertyGroups[] =
    propertiesGroupResult?.data?.listGroups?.map(el => ({
      label: el.name,
      id: String(el.id),
      isProperty: el.isProperty,
      name: el.name,
      parentId: String(el.parentId),
    })) ?? [];

  const handleBackToSettings = () => {
    setAppState("settings");
    setMappings([]);
    setMappingChanges([]);
    setExisingData([]);
    fetchProviders();
  };

  const toggleProviderMappingStatus = async (record: MappingSetting) => {
    const isCurrentlyActive = record.status !== 'inactive';
    const apiStatus = isCurrentlyActive ? "disable" : "enable";

    if (!selectedTenantId) {
      console.error("No tenant selected for activation/deactivation.");
      message.error("Please select a tenant first.");
      return;
    }

    setLoadingStates(prev => ({ ...prev, [record.key]: 'toggle' }));
    const buttonText = isCurrentlyActive ? "Deactivate" : "Activate";
    try {
      await updateMappingStatus(Number(selectedTenantId), record.providerId, apiStatus);
      message.success(`Mapping ${buttonText.toLowerCase()}d successfully!`);
    } catch (error) {
      console.error(`Failed to ${buttonText.toLowerCase()} mapping:`, error);
      message.error(`Failed to ${buttonText.toLowerCase()} mapping.`);
    } finally {
      fetchProviders()
      setLoadingStates(prev => ({ ...prev, [record.key]: null }));
    }
  }

  const fetchTenants = (
    values: GetGroupsListRequest = DEFAULT_TABLE_REQUEST_VALUES
  ) => {
    setIsLoading(true);
    const variables: GetGroupsListRequest = {
      sort: values.sort,
      filters: {
        ...values.filters,
        depth: 1,
        parentId: null
      }
    };

    setIsLoading(false);
    if (result.refetch) {
      result.refetch(variables);
    } else {
      getGroup({
        variables
      });
    }
  };

  const fetchGroupsByProperty = (tenantId: string,
    values: GetGroupsListRequest = {
      sort: 'name:asc',
      filters: {
        depth: 1,
      }
    }
  ) => {
    setIsLoading(true);
    const variables: GetGroupsListRequest = {
      sort: values.sort,
      filters: {
        parentId: Number(tenantId),
        active: ["active"],
        isProperty: [true]
      }
    };

    setIsLoading(false);
    if (propertiesGroupResult.refetch) {
      propertiesGroupResult.refetch(variables);
    } else {
      getPropertyGroup({
        variables
      });
    }
  };

  async function fetchProviders() {
    if (!!selectedTenantId) {
      setIsLoading(true);
      try {
        const tenantName = TENANTS.find(e => e.value === selectedTenantId)?.label ?? "";
        const { data } = await getProvidersForTenant(selectedTenantId, tenantName);
        const formatted = data.map((item: any) => ({
          key: item.provider_id.toString(),
          platform: (
            <div className="svg-wrapper">
              <Icon type="mappingSetting" /> <span>{item.provider_name}</span>
            </div>
          ),
          status: (item.properties_mapped === 0 && item.status === 'incomplete') ? "not started" : item.status,
          propertiesMapped: item.properties_mapped,
          lastUpdated: item.updated_at ? new Date(item.updated_at).toLocaleDateString() : "",
          providerId: item.provider_id
        }));
        setProviderList(formatted);
      } catch (err) {
        console.error("API error:", err);
      } finally {
        setIsLoading(false);
      }
    }
  }

  useEffect(() => {
    fetchTenants();
  }, []);

  useEffect(() => {
    fetchGroupsByProperty(selectedTenantId)
    fetchProviders();
  }, [selectedTenantId]);

  const dataSource: MappingSetting[] = providerList;

  const columns = [
    {
      title: "Platform",
      dataIndex: "platform",
      key: "platform",
      render: (text: string) => (
        <Space align="center" size="middle" className="d-flex">
          {text}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        switch (status.toLowerCase()) {
          case "complete":
            return (
              <span data-slot="badge" className="complete-button">
                <Icon type="completed" />Complete</span>
            );
          case "inactive":
            return (
              <span data-slot="badge" className="warning-button">
                <Icon type="needsReview" />Needs Review</span>

            );
          case "not started":
            return (
              <span data-slot="badge" className="not-started-button">
                <Icon type="notStarted" />Not Started</span>
            );
          case "incomplete":
            return (
              <span data-slot="badge" className="warning-button">
                <Icon type="inComplete" />Needs Review</span>

            );
          default:
            return (
              <span data-slot="badge" className="warning-button">
                <Icon type="needsReview" />
                Needs Review</span>
            );
        }
      },
    },
    {
      title: "Properties Mapped",
      dataIndex: "propertiesMapped",
      key: "propertiesMapped",
      render: (val: string) => (val ? val : <Text type="secondary">—</Text>),
    },
    {
      title: "Last Updated",
      dataIndex: "lastUpdated",
      key: "lastUpdated",
      render: (date: string) => (date ? date : <Text type="secondary">—</Text>),
    },
    {
      title: "Actions",
      key: "action",
      render: (record: MappingSetting) => {
        const isToggleLoading = loadingStates[record.key] === 'toggle';
        const isCurrentlyActive = record.status !== 'inactive';
        return record.status === "not started" && getHasPermission({
          code: PermissionCode.EditMappingTenant
        }).permitted ? (
          <Button
            type="default"
            className="create-mapping-button"
            onClick={() =>
              handleManageMapping(record, selectedTenantId)
            }
            icon={<Icon type="createMapping" className="w-1 h-1" />}
          >
            Create Mapping
          </Button>
        ) : (
          <div className="create-mapping-button-wrapper">
            {getHasPermission({
              code: PermissionCode.EditMappingTenant
            }).permitted ?
              <>
                {record.status !== 'inactive' &&
                  <>
                    <Button
                      className="create-mapping-button"
                      type="default"
                      onClick={() => handleManageMapping(record, selectedTenantId)}
                      icon={<SettingOutlined className="margin-right-8" />}
                    >
                      Manage
                    </Button>
                    {record.status.toLowerCase() !== "complete" &&
                      <Button
                        className="create-mapping-button"
                        type="default"
                        onClick={() => handleUnMapping(record, selectedTenantId)}
                        icon={<Icon type="unMapping" />}
                      >
                        View Unmapped
                      </Button>}
                  </>
                }
                <Button
                  type="default"
                  className="create-mapping-button"
                  icon={isCurrentlyActive ? <PoweroffOutlined className="margin-right-8" /> : <CheckCircleOutlined />}
                  loading={isToggleLoading}
                  onClick={() => toggleProviderMappingStatus(record)}
                >
                  {record.status !== 'inactive' ? 'Deactivate' : 'Activate'}
                </Button>
              </> : record.status !== "not started" ? <Button
                className="create-mapping-button"
                type="default"
                onClick={() => handleView(record, selectedTenantId)}
              >
                View
              </Button> : <></>}

          </div>
        );
      },
    },
  ];

  const handleManageMapping = async (
    record: MappingSetting,
    tenantId: string
  ) => {
    setSelectedTenantId(tenantId);
    setSelectedTenantName(
      TENANTS.find((val) => val.value === String(tenantId))?.label || ""
    );
    setProviderId(record.providerId);
    if (record.status === "not started") {
      setAppState("new-mapping");
    } else {
      setAppState("review-existing");
      setLoadingStates(prev => ({ ...prev, [record.key]: 'manage' }));
      try {
        const properties = await getExistingMappedMerchants(Number(tenantId), record.providerId, "");

        setExisingData(properties);
      } catch (error) {
        message.error("Failed to load properties for mapping.");
      } finally {
        setLoadingStates(prev => ({ ...prev, [record.key]: null }));
      }
    }
    fetchGroupsByProperty(selectedTenantId)
  };


  const handleUnMapping = async (
    record: MappingSetting,
    tenantId: string
  ) => {
    setSubmitting(true);
    setSelectedTenantId(tenantId);
    setSelectedTenantName(
      TENANTS.find((val) => val.value === String(tenantId))?.label || ""
    );
    setProviderId(record.providerId);
    setLoadingStates(prev => ({ ...prev, [record.key]: 'manage' }));
    try {
      const properties = await getExternalMerchants(Number(tenantId), record.providerId, "", "", 0, "mapped");
      const uniqueProperties =
        (propertiesGroupResult.data?.listGroups ?? [])
          .filter(
            el => !properties.some(e => String(e.property_id) === String(el.id))
          )
          .map(el => ({
            id: el.id,
            propertyName: el.name,
          }));
      setInitialVenzaProperties([...uniqueProperties])
      setAppState("unmapped");
    } catch (error) {
      message.error("Failed to load properties for mapping.");
    } finally {
      setSubmitting(false);
      setLoadingStates(prev => ({ ...prev, [record.key]: null }));
    }
    fetchGroupsByProperty(selectedTenantId)
  };

  const handleView = async (
    record: MappingSetting,
    tenantId: string
  ) => {
    setExisingData([]);
    setSelectedTenantId(tenantId);
    setSelectedTenantName(
      TENANTS.find((val) => val.value === String(tenantId))?.label || ""
    );
    setProviderId(record.providerId);
    setAppState("view-mapping");
    setLoadingStates(prev => ({ ...prev, [record.key]: 'manage' }));
    try {
      const properties = await getExistingMappedMerchants(Number(tenantId), record.providerId, "");
      setExisingData(properties);
    } catch (error) {
      message.error("Failed to load properties for mapping.");
    } finally {
      setIsLoading(false);
      setLoadingStates(prev => ({ ...prev, [record.key]: null }));
    }
    fetchGroupsByProperty(selectedTenantId);
  };

  return (
    <div className="mappingWrapper">

      {appState === "view-mapping" &&
        <div>
          <div className="d-flex justify-end mbx-16">
            <Button
              type="text"
              className="backToMappingSettings"
              onClick={handleBackToSettings}
            >
              ← Back to Mapping Settings
            </Button>
          </div>
          <MappingViewTable initialData={existingData} isLoading={isLoading} />
        </div>
      }

      {appState === "review-existing" && (
        <ReviewMappingContainer
          tenantInfo={{
            id: selectedTenantId,
            name: selectedTenantName,
            providerId: providerId ?? 0,
          }}
          handleBackToSettings={handleBackToSettings}
          handleBackToHome={handleBackToSettings}
          initialData={existingData}
          venzaProperties={PROPERTY_TENANTS}
        />
      )}

      {appState === "unmapped" && (

        <Spin spinning={submitting}>
          <div style={{ backgroundColor: "#fff" }}>
            <UnMappingContainer
              status={"incomplete"}
              tenantInfo={{
                id: selectedTenantId,
                name: selectedTenantName,
                providerId: providerId ?? 0,
              }}
              initialVenzaProperties={initialVenzaProperties}
              onBack={handleBackToSettings}
            />
          </div>
        </Spin>
      )}

      {appState === "settings" && (
        <Spin spinning={submitting}>
          <MappingTableContainer
            selectedTenantId={selectedTenantId}
            setSelectedTenantId={setSelectedTenantId}
            TENANTS={TENANTS}
            isSuperAdmin={isSuperAdmin}
            columns={columns}
            isLoading={isLoading}
            dataSource={dataSource}
          />
        </Spin>
      )}

      {appState === "new-mapping" && (
        <CreateMappingContainer
          venzaProperties={PROPERTY_TENANTS}
          tenantInfo={{
            id: selectedTenantId,
            name: selectedTenantName,
            providerId: providerId ?? 0,
          }}
          handleBackToSettings={handleBackToSettings}
          handleBackToHome={handleBackToSettings}
          mappings={mappings}
          mappingChanges={mappingChanges}
        />
      )}
    </div>
  );
};

export default Mappings;
